import { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material'
import { collection, query, where, getDocs, limit } from 'firebase/firestore'
import PersonIcon from '@mui/icons-material/Person'
import { newFirestore } from '../../../../utils/firebase/firebase'

const UserSearch = ({ selectedUser, onUserSelect, disabled }) => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    if (inputValue.length >= 2) {
      searchUsers(inputValue)
    } else {
      setUsers([])
    }
  }, [inputValue])

  const searchUsers = async (searchTerm) => {
    setLoading(true)
    try {
      const usersRef = collection(newFirestore, 'users')

      // Search by first name
      const firstNameQuery = query(
        usersRef,
        where('firstName', '>=', searchTerm),
        where('firstName', '<=', searchTerm + '\uf8ff'),
        limit(10)
      )

      // Search by last name
      const lastNameQuery = query(
        usersRef,
        where('lastName', '>=', searchTerm),
        where('lastName', '<=', searchTerm + '\uf8ff'),
        limit(10)
      )

      // Search by email
      const emailQuery = query(
        usersRef,
        where('email', '>=', searchTerm.toLowerCase()),
        where('email', '<=', searchTerm.toLowerCase() + '\uf8ff'),
        limit(10)
      )

      const [firstNameResults, lastNameResults, emailResults] =
        await Promise.all([
          getDocs(firstNameQuery),
          getDocs(lastNameQuery),
          getDocs(emailQuery),
        ])

      const allUsers = new Map()

      // Combine results and remove duplicates
      ;[firstNameResults, lastNameResults, emailResults].forEach((snapshot) => {
        snapshot.docs.forEach((doc) => {
          const userData = { id: doc.id, ...doc.data() }
          allUsers.set(doc.id, userData)
        })
      })

      setUsers(Array.from(allUsers.values()))
    } catch (error) {
      console.error('Error searching users:', error)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const getOptionLabel = (option) => {
    if (!option) return ''
    return `${option.firstName} ${option.lastName} (${option.email})`
  }

  const renderOption = (props, option) => (
    <Box component='li' {...props}>
      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
      <Box>
        <Typography variant='body1'>
          {option.firstName} {option.lastName}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {option.email}
        </Typography>
      </Box>
    </Box>
  )

  return (
    <Box>
      <Typography variant='body2' color='text.secondary' gutterBottom>
        Associate with customer (optional):
      </Typography>

      <Autocomplete
        value={selectedUser}
        onChange={(_, newValue) => {
          onUserSelect(newValue)
        }}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue)
        }}
        options={users}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        loading={loading}
        disabled={disabled}
        noOptionsText={
          inputValue.length < 2 ? 'Type to search users...' : 'No users found'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Search by name or email...'
            variant='outlined'
            size='small'
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant='outlined'
              label={`${option.firstName} ${option.lastName}`}
              {...getTagProps({ index })}
              key={option.id}
            />
          ))
        }
      />

      {selectedUser && (
        <Box sx={{ mt: 1 }}>
          <Chip
            icon={<PersonIcon />}
            label={`${selectedUser.firstName} ${selectedUser.lastName}`}
            onDelete={() => onUserSelect(null)}
            color='primary'
            variant='outlined'
            disabled={disabled}
          />
        </Box>
      )}
    </Box>
  )
}

export default UserSearch
